import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CloudDownload as CloudDownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GroupWork as GroupWorkIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { 
  getAllowedLabTypes, 
  getLabTypeName, 
  getInitialLabTypeId,
  getFirstLabTypeId, 
  shouldDisableDropdown,
  LAB_TYPES 
} from "../../../../utils/labTypeFilter";

const GroupMaster = () => {
  // Get filtered lab types using utility
  const filteredLabTypes = getAllowedLabTypes(LAB_TYPES);
  
  console.log('GroupMaster - filteredLabTypes:', filteredLabTypes);

  // Helper function to get lab type name by ID
  const getLabTypeNameLocal = (labTypeId) => {
    return getLabTypeName(labTypeId, filteredLabTypes);
  };

  // Helper function to get lab type ID by name
  const getLabTypeIdByName = (labTypeName) => {
    if (!labTypeName) return null;
    const labType = filteredLabTypes.find(
      (l) => l.name.toLowerCase() === labTypeName.toLowerCase()
    );
    return labType ? labType.id : null;
  };

  const [formData, setFormData] = useState({
    id: null,
    groupName: "",
    labTypeId: "",
    disciplineId: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [groups, setGroups] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [filterDisciplines, setFilterDisciplines] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterLabType, setFilterLabType] = useState("");
  const [filterDiscipline, setFilterDiscipline] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  // Fetch filter disciplines when filterLabType changes
  useEffect(() => {
    if (filterLabType) {
      fetchFilterDisciplines(filterLabType);
    } else {
      setFilterDisciplines([]);
      setFilterDiscipline("");
    }
  }, [filterLabType]);

  // Get labTypeId from localStorage and set in form data
  useEffect(() => {
    const initialLabTypeId = getInitialLabTypeId();
    const firstLabTypeId = getFirstLabTypeId();
    
    console.log('Initialization - initialLabTypeId:', initialLabTypeId);
    console.log('Initialization - firstLabTypeId for filter:', firstLabTypeId);
    
    if (initialLabTypeId) {
      const labTypeName = getLabTypeNameLocal(initialLabTypeId);
      console.log(`Initial Lab Type ID: ${initialLabTypeId}, Lab Type: ${labTypeName}`);
      
      setFormData(prev => ({ 
        ...prev, 
        labTypeId: initialLabTypeId 
      }));
      
      // Fetch disciplines for initial lab type
      fetchDisciplinesByLabType(initialLabTypeId);
    }
    
    // Set filter to first available lab type
    if (firstLabTypeId) {
      setFilterLabType(firstLabTypeId);
      console.log('Set labTypeFilter to first available:', firstLabTypeId);
    }
  }, []);

  // Fetch disciplines when labTypeId changes in form
  useEffect(() => {
    if (formData.labTypeId) {
      fetchDisciplinesByLabType(formData.labTypeId);
    }
  }, [formData.labTypeId]);

  const fetchDisciplinesByLabType = async (labTypeId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin-master/disciplines/${labTypeId}`
      );
      if (res.data.status === "success") {
        // Transform data to ensure id is number (API returns string)
        const transformedData = (res.data.data || []).map(item => ({
          ...item,
          id: Number(item.id) || item.id,
          labTypeId: Number(item.labTypeId) || item.labTypeId
        }));
        console.log('Fetched disciplines for lab type', labTypeId, ':', transformedData);
        setDisciplines(transformedData);
      } else {
        setDisciplines([]);
      }
    } catch (err) {
      console.error("Error fetching disciplines:", err);
      setDisciplines([]);
    }
  };

  const fetchFilterDisciplines = async (labTypeId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin-master/disciplines/${labTypeId}`
      );
      if (res.data.status === "success") {
        // Transform data to ensure id is number (API returns string)
        const transformedData = (res.data.data || []).map(item => ({
          ...item,
          id: Number(item.id) || item.id,
          labTypeId: Number(item.labTypeId) || item.labTypeId
        }));
        console.log('Fetched filter disciplines for lab type', labTypeId, ':', transformedData);
        setFilterDisciplines(transformedData);
      } else {
        setFilterDisciplines([]);
      }
    } catch (err) {
      console.error("Error fetching filter disciplines:", err);
      setFilterDisciplines([]);
    }
  };

  const fetchGroups = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin-master/groups-all`,
      );
      if (res.data.status === "success") {
        // Transform API data: convert labType string to labTypeId number and disciplineID to disciplineId number
        const transformedData = (res.data.data || []).map(item => {
          const labTypeId = getLabTypeIdByName(item.labType);
          return {
            ...item,
            labTypeId: labTypeId || item.labTypeId,
            disciplineId: Number(item.disciplineID),  // API returns disciplineID (capital)
          };
        });
        console.log('Fetched groups:', transformedData);
        setGroups(transformedData);
      } else {
        setError(res.data.message || "Failed to fetch Groups");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching Groups");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Reset disciplineId when labTypeId changes
    if (name === "labTypeId") {
      setFormData(prev => ({ ...prev, disciplineId: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.groupName || !formData.labTypeId || !formData.disciplineId) {
      setError("All fields are required");
      return;
    }
    setError("");
    setLoading(true);
    try {
      if (formData.id) {
        // Update
        await axios.put(
          `${process.env.REACT_APP_API_URL}/admin-master/groups/${formData.id}`,
          {
            groupName: formData.groupName,
            labTypeId: formData.labTypeId,
            disciplineId: formData.disciplineId,
            updatedById: localStorage.getItem('orgId') || 0,
          },
        );
        setSuccessMessage("Group Master Updated Successfully");
      } else {
        // Create
        await axios.post(
          `${process.env.REACT_APP_API_URL}/admin-master/groups`,
          {
            groupName: formData.groupName,
            labTypeId: formData.labTypeId,
            disciplineId: formData.disciplineId,
            createdById: localStorage.getItem('orgId') || 0,
          },
        );
        setSuccessMessage("Group Master Added Successfully");
      }
      fetchGroups();
      setTimeout(() => setSuccessMessage(""), 3000);
      handleReset();
    } catch (err) {
      setError(err.response?.data?.message || "Error saving Group");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (row) => {
    const labTypeId = row.labTypeId || getLabTypeIdByName(row.labType);
    
    // Fetch disciplines FIRST so dropdown has options
    if (labTypeId) {
      await fetchDisciplinesByLabType(labTypeId);
    }
    
    // Then set formData with disciplineId as number to match dropdown options
    setFormData({
      id: row.grpId,
      groupName: row.groupName,
      labTypeId: labTypeId,
      disciplineId: Number(row.disciplineId),
    });
    
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin-master/groups/${deleteId}`,
      );
      setSuccessMessage("Group deleted successfully");
      fetchGroups();
    } catch (err) {
      setError(err.response?.data?.message || "Error deleting Group");
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(""), 2000);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleReset = () => {
    const storedLabTypeId = localStorage.getItem('labTypeId');
    setFormData({
      id: null,
      groupName: "",
      labTypeId: storedLabTypeId ? Number(storedLabTypeId) : "",
      disciplineId: "",
    });
    setError("");
  };

  const handleResetFilter = () => {
    setFilterDiscipline('');
    setPage(0);
  };

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin-master/groups/export`,
        {
          params: {
            labTypeId: filterLabType || undefined,
            disciplineId: filterDiscipline || undefined,
          },
        }
      );

      let dataToExport = [];
      if (res.data.status === "success" && res.data.data) {
        dataToExport = res.data.data;
      } else {
        // If API doesn't have export endpoint, use current filtered data
        dataToExport = groups.filter(
          (row) => {
            const matchLabType = !filterLabType || String(row.labTypeId) === String(filterLabType);
            const matchDiscipline = !filterDiscipline || String(row.disciplineId) === String(filterDiscipline);
            return matchLabType && matchDiscipline;
          }
        );
      }

      if (dataToExport.length === 0) {
        setError("There is no data available in this list.");
        setTimeout(() => setError(""), 3000);
        return;
      }

      // Transform data for Excel
      const excelData = dataToExport.map((item, idx) => ({
        "Sr. No.": idx + 1,
        "Group Name": item.groupName,
        "Discipline Name": item.disciplineName || "Unknown",
        "Lab Type": getLabTypeNameLocal(item.labTypeId) || item.labType || "Unknown",
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Groups");
      
      // Auto-size columns
      const colWidths = [
        { wch: 10 },
        { wch: 30 },
        { wch: 30 },
        { wch: 20 },
      ];
      ws['!cols'] = colWidths;
      
      XLSX.writeFile(wb, "Group_Master.xlsx");
      setSuccessMessage("Excel downloaded successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError("Error downloading Excel file");
      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterLabTypeChange = (e) => {
    const newLabType = e.target.value;
    setFilterLabType(newLabType);
    setFilterDiscipline(""); // Reset discipline filter
    setPage(0);
  };

  // Filter groups based on selected filters
  const filteredGroups = groups.filter((row) => {
    const matchLabType = !filterLabType || String(row.labTypeId) === String(filterLabType);
    const matchDiscipline = !filterDiscipline || String(row.disciplineId) === String(filterDiscipline);
    return matchLabType && matchDiscipline;
  });

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5", p: 1 }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* Gradient Header */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            <GroupWorkIcon
              sx={{ fontSize: "2rem", mr: 2, verticalAlign: "middle" }}
            />
            Group Master
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Manage Groups efficiently
          </Typography>
        </Paper>
        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccessMessage("")}
          >
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {/* Form Card */}
        <Card elevation={3} sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <GroupWorkIcon
                sx={{ fontSize: "2rem", color: "#1976d2", mr: 1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#1976d2" }}
              >
                Add / Edit Group
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e9ecef",
                mb: 2,
              }}
            >
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Lab Type <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Select
                      fullWidth
                      name="labTypeId"
                      value={formData.labTypeId}
                      onChange={handleInputChange}
                      displayEmpty
                      required
                      disabled={shouldDisableDropdown(filteredLabTypes)}
                      variant="outlined"
                    >
                      <MenuItem value="" disabled>
                        <em>--Select Lab Type--</em>
                      </MenuItem>
                      {filteredLabTypes.map((lab) => (
                        <MenuItem key={lab.id} value={lab.id}>
                          {lab.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Discipline Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Select
                      fullWidth
                      name="disciplineId"
                      value={formData.disciplineId}
                      onChange={handleInputChange}
                      displayEmpty
                      required
                      disabled={!formData.labTypeId}
                      variant="outlined"
                    >
                      <MenuItem value="" disabled>
                        <em>--Select Discipline--</em>
                      </MenuItem>
                      {disciplines.map((disc) => (
                        <MenuItem key={disc.id} value={disc.id}>
                          {disc.disciplineName}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Group Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="groupName"
                      value={formData.groupName}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      placeholder="Enter Group Name"
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    gap: 3,
                    justifyContent: "center",
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                    sx={{ backgroundColor: "#1976d2" }}
                  >
                    {formData.id ? "Update" : "Save"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RefreshIcon />}
                    disabled={loading}
                  >
                    Reset
                  </Button>
                </Box>
              </form>
            </Box>
          </CardContent>
        </Card>
        {/* Table Card */}
        <Card elevation={3} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <GroupWorkIcon
                sx={{ fontSize: "2rem", color: "#1976d2", mr: 1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#1976d2" }}
              >
                Group List
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RefreshIcon />}
                onClick={handleResetFilter}
                disabled={loading}
                sx={{ minWidth: 140 }}
              >
                Reset Filter
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CloudDownloadIcon />}
                onClick={handleDownloadExcel}
                disabled={loading}
                sx={{ minWidth: 160 }}
              >
                Export Excel
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Filter by Lab Type
              </Typography>
              <Select
                fullWidth
                displayEmpty
                disabled={shouldDisableDropdown(filteredLabTypes)}
                value={filterLabType}
                onChange={handleFilterLabTypeChange}
                renderValue={(selected) => {
                  if (!selected) return <em>--All Lab Types--</em>;
                  const labType = filteredLabTypes.find((l) => l.id === selected);
                  return labType?.name || 'Unknown';
                }}
              >
                <MenuItem value="">
                  <em>--All Lab Types--</em>
                </MenuItem>
                {filteredLabTypes.map((lab) => (
                  <MenuItem key={lab.id} value={lab.id}>
                    {lab.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Filter by Discipline
              </Typography>
              <Select
                fullWidth
                displayEmpty
                value={filterDiscipline}
                onChange={(e) => {
                  setFilterDiscipline(e.target.value);
                  setPage(0);
                }}
                disabled={!filterLabType}
                renderValue={(selected) => {
                  if (!selected) return <em>--All Disciplines--</em>;
                  const disc = filterDisciplines.find((d) => String(d.id) === String(selected));
                  return disc?.disciplineName || 'Unknown';
                }}
              >
                <MenuItem value="">
                  <em>--All Disciplines--</em>
                </MenuItem>
                {filterDisciplines.map((discipline) => (
                  <MenuItem key={discipline.id} value={discipline.id}>
                    {discipline.disciplineName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              Loading...
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell sx={{ width: "8%" }}>
                        <strong>Sr. No.</strong>
                      </TableCell>
                      <TableCell sx={{ width: "12%" }}>
                        <strong>Action</strong>
                      </TableCell>
                       <TableCell sx={{ width: "30%" }}>
                        <strong>Lab Type</strong>
                      </TableCell>
                      <TableCell sx={{ width: "25%" }}>
                        <strong>Discipline Name</strong>
                      </TableCell>
                      <TableCell sx={{ width: "25%" }}>
                        <strong>Group Name</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredGroups.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">
                            No Group data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredGroups
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((row, idx) => {
                          const labType = filteredLabTypes.find(
                            (l) => String(l.id) === String(row.labTypeId),
                          );
                          return (
                            <TableRow key={row.grpId}>
                              <TableCell>
                                {page * rowsPerPage + idx + 1}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEdit(row)}
                                  title="Update"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(row.grpId)}
                                  title="Delete"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                {labType?.name || row.labType || "Unknown"}
                              </TableCell>
                              <TableCell>{row.disciplineName || "Unknown"}</TableCell>
                              <TableCell>{row.groupName || "Unknown"}</TableCell>
                            </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredGroups.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                sx={{ borderTop: "1px solid #e0e0e0" }}
              />
            </>
          )}
        </Card>
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Do you want to Delete this Group?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={loading}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default GroupMaster;
